<template>
  <div ref="component" class="stat-card-white">
    <div class="stat-header-white">
      <span>{{ chartLabel }}</span>
      <div v-if="!loader" class="d-flex align-items-center">
        <div class="expand-block px-2 mb-1 cursor-pointer position-relative" @click.prevent="showtable()">
          <img class="cursor-pointer" src="../../assets/img/table_view.png" alt="Data overview" />
          <div class="more-expand"><span>{{ $t("navigations.data overview") }}</span></div>
        </div>
        <div class="expand-block px-2 mb-1 cursor-pointer" @click.prevent="expandBlock = true">
          <img src="../../assets/img/expand.png" alt="Expand" />
        </div>
        <div class="text-center" v-on:clickout="showVisitPopup = false">
          <div class="position-relative cursor-pointer biggerfont ml-2"
            @click.prevent="showVisitPopup = !showVisitPopup">
            <i class="fa fa-ellipsis-v"></i>
          </div>
          <div class="plan-side-popup" ref="excludeThis" v-if="showVisitPopup">
            <div @click.prevent="showVisitPopup = false, exporttoexcell()">Export to Excel</div>
            <div @click.prevent="exporttopdf()">Export to PDF</div>
            <div @click.prevent="exportDataPdf()">
              Export Data to PDF
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="stat-body-white d-flex flex-column" :class="{ 'flex-column': loader }">
      <!-- Controls -->
      <div v-if="enrolledornot && fromPage === 'patientScreening' && !loader" class="mb-2">
        <label><input type="checkbox" v-model="aiPredictionTrue" @change="updateChart" /> Enrolled</label>
        <label class="ml-3"><input type="checkbox" v-model="aiPredictionFalse" @change="updateChart" />
          Non-enrolled</label>
      </div>
      <div v-if="!loader && requiredOptions" class="d-flex align-items-center mb-2">
        <label v-if="fromPage !== 'studySummary'" class="mb-0 mr-1">{{ $t("navigations.attribute") }}:</label>
        <select v-if="fromPage !== 'studySummary'" class="graph-select mr-3" v-model="selectedXAxis"
          @change="updateChart">
          <option v-for="opt in xAxisOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
        <label v-if="fromPage !== 'studySummary'" class="mb-0 mr-1">{{ $t("navigations.group category") }}:</label>
        <select v-if="fromPage !== 'studySummary'" class="graph-select" v-model="selectedGroupCategory"
          @change="updateChart">
          <option v-for="opt in groupCategoryOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
      </div>

      <!-- Drill controls -->
      <div v-if="!loader && selectedGroups.length" class="d-flex flex-row-reverse gap-2 mb-2 w-100">
        <img class="cursor-pointer" src="../../assets/img/chart_cancel.png" @click.prevent="cancelselection()" />
        <img class="cursor-pointer" src="../../assets/img/chart_submit.png" @click.prevent="recreatechart()" />
      </div>

      <!-- Legend -->
      <div v-if="visibleLegendColorcodes.length && !loader" class="legend-wrap">
          <div v-for="colors in visibleLegendColorcodes" :key="colors.type" class="d-flex">
            <div class="legandbox" :style="`background-color: ${colors.color};`"></div>
            {{ colors.type }}
        </div>
      </div>

      <!-- Chart SVG & Tooltip -->
      <div v-show="!loader" class="position-relative chart-scroll-wrap">
        <svg ref="chartContainer"></svg>
        <div ref="tooltip" class="tooltip"></div>
      </div>

      <!-- Loader -->
      <div v-if="loader" class="loader"></div>
    </div>

    <enlargerpopup v-if="expandBlock" @closeModal="expandBlock = false, createChart()">
      <div class="stat-body-white">
        <svg ref="popupChartContainer"></svg>
      </div>
    </enlargerpopup>
  </div>
</template>

<script>
import * as d3 from 'd3';
import enlargerpopup from '../AiCharts/popupfiles/enlarge_popup.vue';
import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import store from '../../store/index';

export default {
  name: 'ComparativeHorizontalBarChart',
  components: { enlargerpopup },
  props: {
    data: { type: Array, required: true, default: () => [] },
    xAxisValue: { type: String, default: '' },
    yAxisValue: { type: String, default: '' },
    groupCategoryValue: { type: String, default: '' },
    chartLabel: { type: String, default: 'Comparative Analysis' },
    xAxisLabel: { type: String, default: '' },
    yAxisLabel: { type: String, default: '' },
    loader: { type: Boolean, default: false },
    fromPage: { type: String, default: '' },
    enrolledornot: { type: Boolean, default: true },
    width: { type: Number, default: 600 },
    height: { type: Number, default: 400 },
    requiredOptions: { type: Boolean, default: true },
    custcolors: { type: Array, default: () => ['#62B2FD', '#9BDFC4', '#ABF0FF', '#C1D1FF', '#FBD164', '#FDAD73'] },
    colorcodes: { type: Array, default: () => [] },
    templateId: { type: String, default: '' }
  },
  data() {
    return {
      visibleLegendKeys: [],
      showVisitPopup: false,
      expandBlock: false,
      tooltip: null,
      xAxisOptions: [],
      groupCategoryOptions: [],
      selectedXAxis: this.xAxisValue,
      selectedGroupCategory: this.groupCategoryValue,
      selectedGroups: [],
      sortdata: [],
      filterkeyword: '',
      aiPredictionTrue: true,
      aiPredictionFalse: true,
      mappings: {},
      idtoken: ''
    };
  },
  computed: {
  visibleLegendCategories() {
  return this.visibleLegendKeys || [];
},
  visibleLegendColorcodes() {
  const visible = this.visibleLegendCategories;
  return visible.map((cat, i) => {
    const match = this.colorcodes.find(c => c.type === cat);
    return {
      type: cat,
      color: match ? match.color : this.custcolors[i % this.custcolors.length]
    }
  });
}
},
  watch: {
    data: { handler() { this.initOptions(); this.cancelselection(); this.createChart(); }, deep: true },
    expandBlock(newVal) { if (newVal) this.$nextTick(this.createChart); }
  },
  async beforeMount() {
    this.idtoken = store.getters.getIdToken;
    if (this.fromPage === 'patientScreening') await this.loadMappings();
    this.initOptions();
  },
  mounted() {
    this.tooltip = this.$refs.tooltip;
    window.addEventListener('mousemove', this.updateMousePosition);
    this.createChart();
    this.resizeObserver = new ResizeObserver(() => this.createChart());
    this.resizeObserver.observe(this.$refs.chartContainer);
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.updateMousePosition);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    initOptions() {
      if (!this.data.length) return;
      const sample = this.fromPage.startsWith('patient') ? (this.data[0].features || {}) : this.data[0];
      const keys = Object.keys(sample);
      this.xAxisOptions = keys;
      this.groupCategoryOptions = keys;
      this.selectedXAxis = this.xAxisValue || keys[0];
      this.selectedGroupCategory = this.groupCategoryValue || keys[1] || keys[0];
    },
    async loadMappings() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_Service_URL}/forms/templatedesign/getpagesbyid?formId=${this.templateId}&version=1.0`,
          { headers: { Authorization: `Bearer ${this.idtoken}` } }
        );
        res.data.pages.forEach(page =>
          page.field.forEach(fld => {
            if (fld.options) {
              this.mappings[fld.fieldName] = fld.options.reduce((acc, o) => {
                acc[o.codevalue] = o.optionname;
                return acc;
              }, {});
            }
          })
        );
      } catch (e) { console.error(e); }
    },
     toggleBar(categoryKey) {
    // Find all raw items matching this category
    const allForKey = this.data.filter(item => {
      const keyVal = this.fromPage.startsWith('patientScreening')
        ? this.getMappedValue(item.features[this.selectedXAxis], this.selectedXAxis)
        : item[this.selectedXAxis];
      return keyVal === categoryKey;
    });

    const idx = this.selectedGroups.indexOf(categoryKey);
    if (idx > -1) {
      // de‐select
      this.selectedGroups.splice(idx, 1);
      this.sortdata = this.sortdata.filter(r => !allForKey.includes(r));
    } else {
      // select
      this.selectedGroups.push(categoryKey);
      this.sortdata = this.sortdata.concat(allForKey);
    }

    // update the filterkeyword for display
    this.filterkeyword = `${this.selectedXAxis}(${this.selectedGroups.join()})`;

    // redraw bars with new opacity
    this.createChart();
  },

    updateMousePosition(event) {
      const rect = this.$refs.component.getBoundingClientRect();
      this.mouseX = event.clientX - rect.left;
      this.mouseY = event.clientY - rect.top;
    },
    exporttoexcell() {
      this.$emit('exporttoexcell');
    },
    exportDataPdf(){
        this.$emit("exportDataPdf");
    },
    exporttopdf() {
      const excludeEl = this.$refs.excludeThis; if (excludeEl) excludeEl.style.display = 'none';
      html2pdf()
        .set({ margin: 0, filename: `${this.chartLabel}.pdf`, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2 }, jsPDF: { unit: 'pt', format: 'a4', orientation: 'landscape' } })
        .from(this.$refs.component)
        .save();
    },
    showtable() {
      this.$emit('opendrillpopup', this.data);
    },
    cancelselection() {
      this.selectedGroups = [];
      this.sortdata = [];
      this.createChart();
    },
    recreatechart() {
    this.$emit('drilldowncurrentchart', {
      sortedData: this.sortdata,
      filterkeyword: this.filterkeyword
    });
  },
    updateChart() {
      this.cancelselection();
      this.createChart();
      this.$emit('setgroupkeys', { xaxiskey: this.selectedXAxis, groupkey: this.selectedGroupCategory });
    },
    getMapped(val, key) {
      return this.mappings[key]?.[val] || val;
    },
    aggregateData(raw) {
      const arr = raw.filter(d => this.enrolledornot || !d.features?.AIPrediction);
      const group = d3.group(
        arr,
        d => this.fromPage.startsWith('patient') ? this.getMapped(d.features[this.selectedXAxis], this.selectedXAxis) : this.getMapped(d[this.selectedXAxis], this.selectedXAxis),
        d => this.fromPage.startsWith('patient') ? this.getMapped(d.features[this.selectedGroupCategory], this.selectedGroupCategory) : this.getMapped(d[this.selectedGroupCategory], this.selectedGroupCategory)
      );
      return Array.from(group, ([key, sub]) => ({
        key,
        values: Array.from(sub, ([category, records]) => ({
          category,
          value: this.yAxisValue ? d3.sum(records, r => +r[this.yAxisValue] || 0) : records.length
        }))
      }));
    },
   createChart() {
  const containerRef = this.expandBlock
    ? this.$refs.popupChartContainer
    : this.$refs.chartContainer;

  const fixedHeight = 400;
  const margin = { top: 20, right: 20, bottom: 50, left: 130 };
  const totalWidth = this.width;
  const chartAreaHeight = fixedHeight - margin.top - margin.bottom;

  // Aggregate and prepare data
  const data = this.aggregateData(this.data);
  if (!data.length) return;

  // Color & scale
  const visibleCategories = Array.from(
  new Set(data.flatMap(group => group.values.map(v => v.category)))
);
this.visibleLegendKeys = visibleCategories;

const colorScale = d3.scaleOrdinal().domain(visibleCategories).range(this.custcolors);
const customColorMap = new Map(this.colorcodes.map(c => [c.type, c.color]));

  const maxV = d3.max(data, d => d3.max(d.values, v => v.value)) || 0;
  const x = d3.scaleLinear()
    .domain([0, maxV]).nice()
    .range([0, totalWidth - margin.left - margin.right]);

  // --- Calculate dynamic sizes ---
  const numGroups = data.length;
  const numBarsTotal = data.reduce((sum, group) => sum + group.values.length, 0);

  // You can tune these numbers for "minimum" bar height and gap
  const minBarHeight = 12, maxBarHeight = 36;
  const minGap = 1, maxGap = 1;
  const minGroupGap = 10, maxGroupGap = 30;

  // Calculate: total space = (bars x barHeight) + (bars x barGap) + (groups x groupGap)
  // Let’s try to solve for barHeight:
  // Let barHeight = h, barGap = g, groupGap = G

  // We'll first try to assign most space to barHeight, minimal gap
  let barGap = minGap;
  let groupGap = minGroupGap;
  let spaceForBars = chartAreaHeight - (numBarsTotal * barGap) - (numGroups * groupGap);

  // If space is negative, gaps can't be that large
  if (spaceForBars < minBarHeight * numBarsTotal) {
    // Shrink all gaps to minimum possible and let barHeight go as small as needed
    barGap = minGap;
    groupGap = minGroupGap;
    spaceForBars = chartAreaHeight - (numBarsTotal * barGap) - (numGroups * groupGap);
  } else {
    // If extra space, make bars/gaps a little bigger (up to their max)
    // Distribute extra to barHeight first, then to gaps if enough left
    let barHeight = Math.min(maxBarHeight, spaceForBars / numBarsTotal);
    if (barHeight > minBarHeight) {
      // Try to make barGap and groupGap a little larger if possible
      barGap = Math.min(maxGap, (chartAreaHeight - numBarsTotal * barHeight - numGroups * groupGap) / numBarsTotal);
      groupGap = Math.min(maxGroupGap, (chartAreaHeight - numBarsTotal * barHeight - numBarsTotal * barGap) / numGroups);
    }
  }
  // Final barHeight so all fits exactly:
  const barHeight = Math.max(minBarHeight, (chartAreaHeight - (numBarsTotal * barGap) - (numGroups * groupGap)) / numBarsTotal);

  // Clean
  d3.select(containerRef).selectAll("*").remove();
  const svg = d3.select(containerRef)
    .attr("viewBox", `0 0 ${totalWidth} ${fixedHeight}`)
    .attr("height", fixedHeight)
    .attr("preserveAspectRatio", "xMinYMin meet")
    .style("width", "100%")
    .style("display", "block");

  const g = svg.append("g")
    .attr("transform", `translate(${margin.left},${margin.top})`);

  // Y axis line
  g.append("line")
    .attr("x1", 0)
    .attr("x2", 0)
    .attr("y1", 0)
    .attr("y2", chartAreaHeight)
    .attr("stroke", "#666")
    .attr("stroke-width", 1.2);

  // Bars and labels (fit in available space)
  let currentY = 0;
  data.forEach((group) => {
    g.append("text")
      .attr("x", -10)
      .attr("y", currentY + (group.values.length * (barHeight + barGap) - barGap) / 2)
      .attr("text-anchor", "end")
      .attr("alignment-baseline", "middle")
      .style("font-size", "13px")
      .text(group.key.length > 18 ? group.key.slice(0,18) + "…" : group.key);

    group.values.forEach((bar) => {
      g.append("rect")
        .attr("x", 0)
        .attr("y", currentY)
        .attr("width", x(bar.value))
        .attr("height", barHeight)
        .attr("fill", customColorMap.get(bar.category) || colorScale(bar.category))
        .style("cursor", "pointer")
        .attr("opacity", this.selectedGroups.length
          ? (this.selectedGroups.indexOf(group.key) > -1 ? 1 : 0.35)
          : 1)
        .on("mouseover", (event) => {
          const groupKey = group.key != null ? group.key : "Null";
          const axisLabel = this.xAxisLabel || this.selectedXAxis || "Group";
          this.tooltip.style.opacity = 1;
          this.tooltip.innerHTML =
            `${axisLabel}: ${groupKey}<br/>` +
            `Category: ${bar.category}<br/>Value: ${bar.value}`;
          const containerRect = this.$refs.component.getBoundingClientRect();
          this.tooltip.style.left = (event.clientX - containerRect.left + 10) + "px";
          this.tooltip.style.top = (event.clientY - containerRect.top - 10 + this.$refs.component.scrollTop) + "px";
        })
        .on("mouseout", () => this.tooltip.style.opacity = 0)
        .on("click", () => this.toggleBar(group.key));

      g.append("text")
        .attr("x", x(bar.value) - 5)
        .attr("y", currentY + barHeight / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", "end")
        .style("font-size", "13px")
        .text(bar.value);

      currentY += barHeight + barGap;
    });
    currentY += groupGap;
  });

  // X axis at the bottom of bars
  g.append("g")
    .attr("transform", `translate(0,${chartAreaHeight})`)
    .call(d3.axisBottom(x));

  // X axis label
  g.append("text")
    .attr("x", (totalWidth - margin.left - margin.right) / 2)
    .attr("y", chartAreaHeight + 40)
    .attr("text-anchor", "middle")
    .style("font-size", "14px")
    .text(this.yAxisLabel);
},
  }
};
</script>

<style scoped>
@import "../AiCharts/charts.css";

.legandbox {
  width: 15px;
  height: 15px;
  margin-right: 5px;
}
.stat-card-white {
  width: 100%;
}
.loader {
  border: 6px solid #f3f3f3;
  border-top: 6px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
.chart-scroll-wrap {
  width: 100%;
  position: relative;
}
.tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 5px;
  border-radius: 3px;
  font-size: 12px;
  opacity: 0;
}
.legend-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px; /* row gap, column gap */
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 10px;
  /* Optional: add padding or a max-width if needed */
}

.legend-wrap > div {
  display: flex;
  align-items: center;
  min-width: 120px; /* Optional: can be tuned or removed */
  margin-bottom: 2px; /* minor tweak for multi-row */
  /* You can also add padding here for more spacing */
}

.legandbox {
  width: 15px;
  height: 15px;
  margin-right: 7px;
  flex-shrink: 0;
}

</style>
